import roles, { rolesDisplayLabel, TUserRole } from '@/constants/roles';
import { getUserRolesToShow } from '@/utils/stringUtils';
import { Badge, HStack, Text, Tooltip } from '@chakra-ui/react';

// Table cells clip overflow, so extra roles collapse into a "+N" badge.
const MAX_VISIBLE_ROLES = 2;

// Roles are categories, not states: kept neutral so the status column stands
// out. Privileged roles get a faint tint to make them easy to spot.
const privilegedRoles: TUserRole[] = [roles.superAdmin, roles.orgAdmin];

const RoleBadge = ({ role }: { role: TUserRole }) => {
  const isPrivileged = privilegedRoles.includes(role);

  return (
    <Badge
      variant={'outline'}
      colorScheme={isPrivileged ? 'purple' : 'gray'}
      backgroundColor={isPrivileged ? 'primary.100' : 'transparent'}
      borderRadius={'full'}
      paddingX={'8px'}
      paddingY={'1px'}
      textTransform={'none'}
      fontSize={'12px'}
      fontWeight={600}
    >
      {rolesDisplayLabel[role]}
    </Badge>
  );
};

const UserRoleBadges = ({ userRoles }: { userRoles?: TUserRole[] }) => {
  const rolesToShow = getUserRolesToShow(userRoles ?? []);

  if (rolesToShow.length === 0) return <Text>/</Text>;

  const visibleRoles = rolesToShow.slice(0, MAX_VISIBLE_ROLES);
  const hiddenRoles = rolesToShow.slice(MAX_VISIBLE_ROLES);

  return (
    <HStack spacing={'6px'}>
      {visibleRoles.map((role) => (
        <RoleBadge key={role} role={role} />
      ))}
      {hiddenRoles.length > 0 && (
        <Tooltip hasArrow label={rolesToShow.map((role) => rolesDisplayLabel[role]).join(', ')}>
          <Badge
            variant={'subtle'}
            colorScheme={'gray'}
            borderRadius={'full'}
            paddingX={'8px'}
            paddingY={'1px'}
            fontSize={'12px'}
            fontWeight={600}
            cursor={'help'}
          >
            +{hiddenRoles.length}
          </Badge>
        </Tooltip>
      )}
    </HStack>
  );
};

export default UserRoleBadges;
